import {Injectable} from '@angular/core';
import {VoucherRepository} from "../repositories/voucher.repository";
import {MbDatatablesTemplate} from "../components/mb-datatable/mb-datatables-template";
import {DataManipulationService} from "./common/data-manipulation.service";
import {Dtos} from "../utils/dtos";
import {FilterService} from "./filterer/filter.service";
import {BaseService} from "./common/base.service";
import {ValidatorService} from "./common/validator.service";
import {Utilities} from "../utils/utilities";
import {MbColDefinition, MbColInput} from "../components/mb-datatable/models/mb-datatable.model";

@Injectable({
    providedIn: 'root'
})
export class VoucherService extends BaseService<VoucherRepository> {


    constructor(repo: VoucherRepository, filterService: FilterService, dataManipulationService: DataManipulationService, validatorService: ValidatorService) {
        super(repo, filterService, dataManipulationService, validatorService);
        this.typeName = 'Voucher';
    }

    getAllNotDeletedVoucherForAnalytics() {
        return super.getAll(Dtos.VOUCHER.DashboardAnalyticsVoucherDTO);
    }

    findById(id: any) {
        const filter = this.filterService.filter(Dtos.VOUCHER.VoucherToDashboardListDTO)
            .equal('id', id)
            .create();
        return this.repo.getAllByFilter(filter);
    }

    public load(startDate: Date, endDate: Date) {
        const filter = this.filterService.filter(Dtos.VOUCHER.VoucherToDashboardListDTO)
            .greaterThanEqual('createdAt', Utilities.dateToBackendString(startDate))
            .and()
            .lessThanEqual('createdAt', Utilities.dateToBackendString(endDate))
            .create();
        return this.repo.getAllByFilter(filter);
    }

    public getByProductNumber(productNumber: string) {
        const filter = this.filterService.filter(Dtos.VOUCHER.VoucherToDashboardListDTO)
            .equal('productNumber', productNumber)
            .create();

        return this.repo.getAllByFilter(filter);
    }

    public findByProductNumberAndDeliveryReservationIsNull(productNumber: string) {
        return this.repo.findByProductNumberAndDeliveryReservationIsNull(productNumber);
    }

    public findByProductNumberAndReservationExtraIsNull(productNumber: string) {
        return this.repo.findByProductNumberAndReservationExtraIsNull(productNumber)
    }

    public saveNewValidity(voucher) {
        if (typeof voucher.validity == "string") voucher.validity = new Date(voucher.validity);
        return this.repo.saveNewValidity(voucher.validity, voucher.productNumber)
    }

    public reject(productNumber) {
        return this.repo.decline(productNumber);
    }

    public redeem(productNumber, restaurantHistoryId: number) {
        return this.repo.redeem(productNumber, restaurantHistoryId)
    }

    convertVoucher(voucher: any, selectedGiftCardId: number, personCount: number) {
        return this.repo.convertVoucher(voucher.productNumber, selectedGiftCardId, personCount)
    }

    declineAndRegenerateVoucher(productNumber: string, resendEmail: string) {
        return this.repo.declineAndRegenerate(productNumber, resendEmail);
    }

    declineVoucher(productNumber: string) {
        return this.repo.decline(productNumber);
    }

    public addExtraDataForOne(voucher: any): any {
        voucher.formattedProductNumber = this.getFormattedProductName(voucher);
        voucher.validityDate = new Date(voucher.validity);
        voucher.isGift = (voucher.recipient != null);
        if (voucher.billingData != null) {
            this.dataManipulationService.addNameAttributeFromFirstAndLastName(voucher.billingData);
            voucher.billingName = voucher.billingData.name;
        }
        return voucher;
    }


    defineColumns(extraInfo?: any): any[] {
        return this.defineModelSpecificColumns(extraInfo);
    }

    protected defineModelSpecificColumns(extraInfo?: any): any[] {
        return [
            MbColDefinition.new('id', 'base.id'),
            MbColDefinition.new('status', 'voucher.status', MbColInput.new(MbColInput.SELECT, [
                {label: 'enum.usable', value: 'usable'},
                {label: 'enum.reject', value: 'reject'},
                {label: 'enum.resend', value: 'resend'},
                {label: 'enum.used', value: 'used'},
                {label: 'enum.forwarded', value: 'forwarded'},
                {label: 'enum.converted', value: 'converted'},
                {label: 'enum.preparedForTable', value: 'preparedForTable'}
            ]), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('formattedProductNumber', 'voucher.productNumber'),
            MbColDefinition.new('product.name', 'voucher.productName'),
            MbColDefinition.new('personCount', 'voucher.personCount'),
            MbColDefinition.new('validity', 'voucher.validity', MbColInput.newDefaultText(), MbDatatablesTemplate.DATETIME_TEMPLATE),
            MbColDefinition.new('restaurantName', 'voucher.restaurant'),
            MbColDefinition.new('billingName', 'voucher.customerName'),
            MbColDefinition.new('user.email', 'voucher.customerEmail'),
            MbColDefinition.new('isGift', 'voucher.isGift', MbColInput.new(MbColInput.SELECT, [
                {label: 'general.true', value: 'true'},
                {label: 'general.false', value: 'false'}
            ]), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
        ];
    }

    public getFormattedProductName(voucher) {
        let string = "";
        for (let index = 0, data = ''; data = voucher.productNumber.charAt(index); index++) {
            if ((index) % 4 === 0 && index > 0) {
                string += "-" + data;
            } else {
                string += data;
            }
        }
        return string.substr(0, 9) + '...';
    };

}
